import React, { useState } from "react";
import { useRestaurant } from "../context/RestaurantContext";
import arrow_left from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import edit from "../assets/images/navbar/edit.png";
import locationIcon from "../assets/images/location.png";
import clock from "../assets/images/icons/orange-clock.png";
import car from "../assets/images/icons/car.png";
import cart from "../assets/images/navbar/cart.png";
import logo from "../assets/images/logo.png";
import EditOutletInfoModal from "../modal/EditOutletInfoModal";

type OutletInfoProps = {
  setIsOutletInfoOpen: (open: boolean) => void;
};

const getDeliveryLabel = (deliveryType?: string) => {
  if (deliveryType === "SELF") return "Self Delivery";
  if (deliveryType === "THIRD_PARTY") return "3rd Party";
  return "Delivery";
};

const OutletInfo = ({ setIsOutletInfoOpen }: OutletInfoProps) => {
  const { restaurantProfile, isLoadingProfile, isRestaurantOpen, refreshProfile } =
    useRestaurant();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const status = isRestaurantOpen ? "open now" : "closed";
  const outletName = restaurantProfile?.businessName || "Kitchen";
  const location = [restaurantProfile?.location?.address, restaurantProfile?.location?.city]
    .filter(Boolean)
    .join(", ") || "Location not set";
  const heroImage = restaurantProfile?.kitchenPhotos?.[0] || restaurantProfile?.brandLogo || logo;
  const featureTags = [
    restaurantProfile?.deliveryAvailable && "Free Delivery",
    restaurantProfile?.parkingAvailable && "Parking",
    (restaurantProfile?.acAvailable === true ||
      String(restaurantProfile?.acAvailable || "").toLowerCase() === "yes" ||
      String(restaurantProfile?.acAvailable || "").toLowerCase() === "full") &&
      "Full AC",
  ].filter(Boolean) as string[];

  const contacts = [
    restaurantProfile?.ownerName
      ? {
          name: restaurantProfile.ownerName,
          role: "Owner",
          phone: restaurantProfile?.alternateContact || restaurantProfile?.primaryContactNumber || "",
        }
      : null,
    restaurantProfile?.alternateContact &&
    restaurantProfile?.alternateContact !== restaurantProfile?.primaryContactNumber
      ? {
          name: "Contact",
          role: "Manager",
          phone: restaurantProfile.alternateContact,
        }
      : null,
  ].filter(Boolean) as { name: string; role: string; phone: string }[];

  const uniqueContacts = contacts.filter(
    (contact, index, arr) => arr.findIndex((item) => item.phone === contact.phone) === index
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
      onClick={() => setIsOutletInfoOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 transform transition-transform duration-300 ease-in-out rounded-l-[20px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] overflow-y-auto transition-colors"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full inline-flex flex-col items-start rounded-l-[24px] bg-white dark:bg-gray-900 shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] p-2 transition-colors">
          <div className="w-full">
            <div className="w-full flex justify-between items-center pb-[12px] border-b dark:border-gray-700 transition-colors">
              <div className="flex items-center gap-3 ">
                <img
                  src={arrow_left}
                  alt="back"
                  className="flex w-[36px] h-[36px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] dark:border-gray-600 bg-[rgba(255,255,255,0.25)] dark:bg-gray-800 cursor-pointer transition-colors"
                  onClick={() => setIsOutletInfoOpen(false)}
                />
                <h2 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[20px] font-semibold leading-[140%] transition-colors">
                  Outlet Info
                </h2>
              </div>
              <img
                src={cancel}
                alt="close"
                className="flex w-[36px] h-[36px] p-[7.5px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] dark:border-gray-600 bg-[rgba(255,255,255,0.25)] dark:bg-gray-800 cursor-pointer transition-colors"
                onClick={() => setIsOutletInfoOpen(false)}
              />
            </div>

            {isLoadingProfile && !restaurantProfile ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8A00]"></div>
              </div>
            ) : (
              <div className="w-full space-y-4 mt-4">
                <div className="relative w-full rounded-[16px] overflow-hidden">
                  <img
                    src={heroImage}
                    alt="Restaurant"
                    className="w-full h-[200px] object-cover bg-gray-100"
                  />
                  {featureTags.length > 0 && (
                    <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                      {featureTags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="rounded-[8px] border border-[rgba(255,255,255,0.40)] bg-[rgba(255,255,255,0.20)] shadow-[inset_12px_30px_42px_0_rgba(90,90,90,0.03)] backdrop-blur-[11.5px] px-[12px] py-[10px] text-white"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div
                    className={`absolute top-8 right-0 inline-flex justify-center items-center py-[5px] px-[11px] gap-[11px] rounded-l-[4.267px] ${status === "open now" ? "bg-[#f3a64e]" : "bg-[#887662]"}`}
                  >
                    <p
                      className={`${status === "open now" ? "text-white" : "text-black"} font-Urbanist text-[12.8px] font-semibold leading-[140%]`}
                    >
                      {status}
                    </p>
                  </div>
                </div>

                <div className="border-2 dark:border-gray-700 p-2 rounded-xl bg-white dark:bg-gray-800 transition-colors">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {restaurantProfile?.brandLogo ? (
                        <img
                          src={restaurantProfile.brandLogo}
                          alt="logo"
                          className="w-12 h-12 rounded-full object-cover border dark:border-gray-600"
                        />
                      ) : null}
                      <div>
                        <h3 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[22px] font-semibold leading-[140%] transition-colors">
                          {outletName}
                        </h3>
                        {restaurantProfile?.restaurantCategory ? (
                          <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[13px]">
                            {restaurantProfile.restaurantCategory}
                          </p>
                        ) : null}
                        <div className="flex items-center gap-1 mt-1">
                          <img
                            src={locationIcon}
                            alt="Location"
                            className="h-[20px] w-[20px] dark:invert"
                          />
                          <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[14px] font-normal leading-[20px] transition-colors">
                            {location}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className="flex items-center justify-center cursor-pointer"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      <img src={edit} alt="Edit" className="w-[24px] h-[24px] dark:invert -mt-[47px]" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-2">
                    {restaurantProfile?.deliveryAvailable ? (
                      <div className="flex gap-2 border dark:border-gray-700 rounded-xl p-2 bg-white dark:bg-gray-800 transition-colors">
                        <img src={car} alt="delivery" className="h-[20px] w-[20px] dark:invert" />
                        <span className="text-[12px] text-[#050505] dark:text-gray-200 transition-colors">
                          {getDeliveryLabel(restaurantProfile?.deliveryType)}
                        </span>
                      </div>
                    ) : null}
                    {restaurantProfile?.avgPreparationTime ? (
                      <div className="flex gap-2 border dark:border-gray-700 rounded-xl p-2 bg-white dark:bg-gray-800 transition-colors">
                        <img src={clock} alt="prep" className="h-[20px] w-[20px]" />
                        <span className="text-[12px] text-[#050505] dark:text-gray-200 transition-colors">
                          {restaurantProfile.avgPreparationTime} min prep
                        </span>
                      </div>
                    ) : null}
                    {restaurantProfile?.minimumOrderValue ? (
                      <div className="flex gap-2 border dark:border-gray-700 rounded-xl p-2 bg-white dark:bg-gray-800 transition-colors">
                        <img src={cart} alt="min order" className="h-[20px] w-[20px] dark:invert" />
                        <span className="text-[12px] text-[#050505] dark:text-gray-200 transition-colors">
                          Min ₹{restaurantProfile.minimumOrderValue}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <hr className="border-2 border-dashed border-[#f3a64e] w-full h-[0.5px] mt-4"/>

                  <div className="mt-4">
                    <h4 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[18px] font-semibold leading-[140%] mb-4 transition-colors">
                      Contact Details
                    </h4>
                    <div className="space-y-2">
                      {uniqueContacts.length > 0 ? (
                        uniqueContacts.map((contact, index) => (
                          <div key={`${contact.phone}-${index}`}>
                            <div className="flex justify-between items-end bg-[#F5F5F5] dark:bg-gray-700 rounded-[12px] p-4 transition-colors">
                              <div>
                                <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-medium leading-[140%] transition-colors">
                                  {contact.name}
                                </p>
                                <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[140%] transition-colors">
                                  {contact.role}
                                </p>
                              </div>
                              {contact.phone ? (
                                <a
                                  href={`tel:${contact.phone}`}
                                  className="text-[#e09239] dark:text-[#FF8A00] font-Urbanist text-[14px] font-semibold leading-[140%] transition-colors"
                                >
                                  {contact.phone}
                                </a>
                              ) : null}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-[#6A6A6A] dark:text-gray-400 text-sm">
                          No contact details available.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {isEditModalOpen && (
          <EditOutletInfoModal
            setIsEditModalOpen={(open) => {
              setIsEditModalOpen(open);
              if (!open) {
                refreshProfile();
              }
            }}
            restaurantProfile={restaurantProfile}
          />
        )}
      </div>
    </div>
  );
};

export default OutletInfo;
